/**
 * 商务风尚模版 - 登录页
 *
 * 设计理念：深墨绿品牌区 + 白色商务卡片
 * 与 sunset（暖色渐变 + 右侧浮窗圆角卡片）完全不同的排版骨架：
 * - 左侧：深墨绿品牌区（金色装饰 + 特性展示 + 数据点缀）
 * - 右侧：白色方角商务卡片（金色 focus 描边 + 细边框）
 */

import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useAuth } from "@/hooks/useAuth";

export default function ExecutiveLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { siteName: contextSiteName } = useSite();
  const { siteConfig } = useSiteConfig();

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const siteName = contextSiteName || siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.username.trim() || !formData.password.trim()) {
      setError("请填写用户名和密码");
      return;
    }
    setSubmitting(true);
    try {
      await login(formData.username, formData.password);
      navigate("/");
    } catch (err: any) {
      setError(err?.message || "登录失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-[#F5F6F4]">
      {/* ── 左侧品牌区：深墨绿 ── */}
      <div className="hidden lg:flex w-[55%] relative flex-col items-center justify-center p-12 bg-gradient-to-br from-[#1F4E3D] via-[#1A4334] to-[#12382A] overflow-hidden">
        {/* 金色装饰线 */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#C2A04B] via-[#DDC583] to-[#C2A04B]" />
        {/* 纹理 */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 14px)" }} />
        {/* 光晕 */}
        <div className="absolute top-16 right-16 w-72 h-72 rounded-full bg-[#C2A04B]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#C2A04B]/5 blur-3xl" />

        <div className="relative z-10 max-w-md">
          {/* Logo */}
          <div className="mb-10">
            {logo ? (
              <img src={logo} alt={siteName} className="h-14" />
            ) : (
              <div className="w-20 h-20 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] flex items-center justify-center text-white text-3xl font-black shadow-2xl shadow-[#C2A04B]/30 border border-[#DDC583]/40">
                {siteName ? siteName.charAt(0).toUpperCase() : 'E'}
              </div>
            )}
          </div>

          {siteName ? (
            <>
              <h1 className="text-4xl font-black text-white mb-4 leading-tight">
                欢迎回到<br /><span className="text-[#DDC583]">{siteName}</span>
              </h1>
              <p className="text-white/60 leading-relaxed text-sm">
                登录您的账户，管理发布的信息，享受更多会员服务
              </p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-black text-white mb-4 leading-tight">
                欢迎<span className="text-[#DDC583]">回来</span>
              </h1>
              <p className="text-white/60 leading-relaxed text-sm">
                登录后即可管理您的分类信息
              </p>
            </>
          )}

          {/* 特性展示 */}
          <div className="mt-12 grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/5 rounded-sm p-4 border border-[#C2A04B]/20 backdrop-blur-sm">
              <div className="w-9 h-9 rounded-sm bg-[#C2A04B] flex items-center justify-center mb-3">
                <i className="ri-file-list-line text-[#1F4E3D]" />
              </div>
              <div className="text-sm font-semibold text-white">发布管理</div>
              <div className="text-xs text-white/50 mt-1">添加、编辑您的分类信息</div>
            </div>
            <div className="bg-white/5 rounded-sm p-4 border border-[#C2A04B]/20 backdrop-blur-sm">
              <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#A8863A] flex items-center justify-center mb-3">
                <i className="ri-vip-crown-line text-[#1F4E3D]" />
              </div>
              <div className="text-sm font-semibold text-white">会员特权</div>
              <div className="text-xs text-white/50 mt-1">享受置顶、加V等专属服务</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 右侧登录卡片 ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-md shadow-2xl shadow-[#1F4E3D]/10 border border-[#E2E8E0] p-8 sm:p-10">
            {/* 移动端Logo */}
            <div className="lg:hidden mb-6 text-center">
              {logo ? (
                <img src={logo} alt="" className="h-10 mx-auto mb-2" />
              ) : (
                <div className="w-14 h-14 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] mx-auto flex items-center justify-center text-white text-xl font-black shadow-lg shadow-[#C2A04B]/20">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'E'}
                </div>
              )}
              {siteName && <p className="text-sm text-gray-500 mt-2">{siteName}</p>}
            </div>

            <div className="flex items-center gap-2 mb-1">
              <span className="w-1 h-6 bg-[#C2A04B] rounded-full" />
              <h2 className="text-2xl font-black text-[#1F4E3D]">登录账户</h2>
            </div>
            <p className="text-sm text-gray-400 mb-8">
              还没有账号？{" "}
              <Link to="/register" className="text-[#8C6E2E] hover:text-[#C2A04B] font-semibold">
                立即注册
              </Link>
            </p>

            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-sm text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line" />{error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">用户名 / 手机号</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-user-line text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="请输入用户名"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#F7F8F6] border border-[#E2E8E0] rounded-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C2A04B] focus:ring-4 focus:ring-[#C2A04B]/10 transition-all text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="请输入密码"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#F7F8F6] border border-[#E2E8E0] rounded-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C2A04B] focus:ring-4 focus:ring-[#C2A04B]/10 transition-all text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-gray-500 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded accent-[#1F4E3D]" />
                  记住我
                </label>
                <Link to="/forgot-password" className="text-[#8C6E2E] hover:text-[#C2A04B] font-medium">
                  忘记密码？
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all shadow-lg shadow-[#C2A04B]/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />登录中...
                  </span>
                ) : "立即登录"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-[#C2A04B] inline-flex items-center gap-1 transition-colors">
                <i className="ri-arrow-left-line" />返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
